import React, {InputHTMLAttributes, useState} from 'react';
import cn from 'classnames';
import Link from '@/_application/core/components/ui/link';
import {Eye} from '@/_application/icons/eye-icon';
import {EyeOff} from '@/_application/icons/eye-off-icon';
import {useTranslation} from 'next-i18next';

export interface Props extends InputHTMLAttributes<HTMLInputElement> {
    className?: string;
    inputClassName?: string;
    label: string;
    name: string;
    forgotPageLink?: string;
    shadow?: boolean;
    variant?: 'normal' | 'solid' | 'outline';
    dimension?: 'small' | 'medium' | 'big';
    error: string | undefined;
    forgotPageRouteOnClick?: () => void;
    required?: boolean;
    registerPageRouteOnClick?: () => void;
}

const variantClasses = {
    normal: 'bg-KPZ_Gray-06 border border-border-base focus:shadow focus:bg-primary-0 focus:border-accent',
    solid: 'bg-KPZ_Gray-06 border border-border-100 focus:bg-primary-0 focus:border-accent',
    outline: 'border border-border-base focus:border-accent',
};

const sizeClasses = {
    small: 'text-sm h-10',
    medium: 'h-12',
    big: 'h-14',
};

const PasswordInput = React.forwardRef<HTMLInputElement, Props>(
    (
        {
            className,
            inputClassName,
            label,
            name,
            error,
            children,
            variant = 'normal',
            dimension = 'medium',
            shadow = false,
            type = 'text',
            forgotPageLink = '',
            forgotPageRouteOnClick,
            required,
            registerPageRouteOnClick,
            ...rest
        },
        ref,
    ) => {
        const {t} = useTranslation();
        const [show, setShow] = useState(false);

        return (
            <div className={className}>
                <div className="mb-2 flex items-center justify-between">
                    <label htmlFor={name} className="text-sm font-semibold text-body">
                        {label}
                        {required && <span className="text-red-500">*</span>}
                    </label>

                    {forgotPageLink && (
                        <Link
                            href={forgotPageLink}
                            className="text-xs text-accent transition-colors duration-200 hover:text-accent-hover focus:font-semibold focus:text-accent-700 focus:outline-none">
                            {t('common:text-forgot-password')}
                        </Link>
                    )}
                </div>
                <div className="relative">
                    <input
                        id={name}
                        data-testid="password-input"
                        name={name}
                        type={show ? 'text' : 'password'}
                        ref={ref}
                        className={cn(
                            'w-full appearance-none rounded py-3 text-sm text-heading transition duration-300 ease-in-out focus:outline-none focus:ring-0 ltr:pl-4 ltr:pr-11 rtl:pl-11 rtl:pr-4',
                            shadow && 'focus:shadow',
                            variantClasses[variant],
                            sizeClasses[dimension],
                            inputClassName,
                        )}
                        aria-label="Nhập mật khẩu"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        {...rest}
                    />
                    <span
                        className="absolute top-5 -mt-2 cursor-pointer text-body ltr:right-4 rtl:left-4"
                        onClick={() => setShow(prev => !prev)}
                        data-testid="eye-button">
                        {show ? <EyeOff className="h-6 w-6" /> : <Eye className="h-6 w-6" />}
                    </span>
                </div>
                {error && <p className="my-2 text-xs text-red-500">{error}</p>}
                <div className=" mt-5 flex justify-between">
                    {registerPageRouteOnClick && (
                        <button
                            aria-label="Đăng ký"
                            onClick={registerPageRouteOnClick}
                            type="button"
                            className="text-xs text-primary transition-colors duration-200 hover:text-KPZ_turquoise focus:font-semibold focus:text-accent-700 focus:outline-none">
                            {t('text-register', 'Đăng ký')}
                        </button>
                    )}

                    {forgotPageRouteOnClick && (
                        <button
                            aria-label="Quên mật khẩu"
                            onClick={forgotPageRouteOnClick}
                            type="button"
                            className="text-xs text-primary transition-colors duration-200 hover:text-KPZ_turquoise focus:font-semibold focus:text-accent-700 focus:outline-none">
                            {t('common:text-forgot-password')}
                        </button>
                    )}
                </div>
            </div>
        );
    },
);
PasswordInput.displayName = 'PasswordInput';
export default PasswordInput;
